/* global Office, Word */
/**
 * Documento gravado com a conversa: captura e restauração no Word.
 * O formato (e por que existem dois) está em `util/documentSnapshot.ts`.
 */
import { docxSnapshotBase64, encodeDocxSnapshot, fingerprintText, isDocxSnapshot, } from '../util/documentSnapshot';
import { applyTemplate } from './template';
import { withTrackChangesSuspended } from './tools';
/** Maior fatia que o Office aceita fora do iPad. */
const SLICE_BYTES = 4 * 1024 * 1024;
/**
 * Propriedade personalizada que diz de QUAL conversa este documento é.
 *
 * É o que deixa a retomada distinguir "o documento desta conversa, reaberto"
 * (mantém o que está na tela — pode ter edições manuais e Desfazer que o banco
 * não viu) de "outro documento" (aí sim, trocar pelo gravado exige confirmar).
 * Mesmo mecanismo do esqueleto (`miau.skeleton`): metadado do arquivo, viaja
 * com ele quando o usuário salva.
 */
const CONVERSATION_PROPERTY = 'miau.conversation';
/** Id da conversa gravado no documento aberto, ou `null`. Best-effort. */
export async function readLinkedConversationId() {
    try {
        return await Word.run(async (context) => {
            const prop = context.document.properties.customProperties.getItemOrNullObject(CONVERSATION_PROPERTY);
            prop.load('value,isNullObject');
            await context.sync();
            if (prop.isNullObject)
                return null;
            const valor = typeof prop.value === 'string' ? prop.value.trim() : '';
            return valor || null;
        });
    }
    catch (e) {
        console.warn('[conversationDocument] não consegui ler o vínculo do documento:', e);
        return null;
    }
}
/**
 * Grava no documento o id da conversa — só quando muda: toda escrita de
 * propriedade marca o arquivo como modificado, e isso não pode acontecer a
 * cada salvamento. Best-effort (host sem a API segue sem vínculo).
 */
export async function linkDocumentToConversation(conversationId) {
    try {
        return await Word.run(async (context) => {
            const props = context.document.properties.customProperties;
            const atual = props.getItemOrNullObject(CONVERSATION_PROPERTY);
            atual.load('value,isNullObject');
            await context.sync();
            if (!atual.isNullObject && atual.value === conversationId)
                return true;
            props.add(CONVERSATION_PROPERTY, conversationId);
            await context.sync();
            return true;
        });
    }
    catch (e) {
        console.warn('[conversationDocument] não consegui vincular o documento à conversa:', e);
        return false;
    }
}
/**
 * Impressão digital do texto do documento aberto (ver `fingerprintText`).
 * `null` quando o host não entrega o texto — quem chama trata como
 * "não sei", nunca como "igual".
 */
export async function readDocumentFingerprint() {
    try {
        return await Word.run(async (context) => {
            var _a;
            const body = context.document.body;
            body.load('text');
            await context.sync();
            return fingerprintText((_a = body.text) !== null && _a !== void 0 ? _a : '');
        });
    }
    catch (e) {
        console.warn('[conversationDocument] não consegui ler o texto do documento:', e);
        return null;
    }
}
/**
 * O documento aberto como .docx inteiro (`docx:base64,...`), com os metadados
 * de `meta` no cabeçalho.
 *
 * `getFileAsync` devolve o arquivo como o Word o salvaria, com o estado em
 * memória — não o que está em disco. Levanta se o host não entregar o arquivo;
 * quem chama decide o que fazer.
 */
export function captureDocumentFile(meta = {}) {
    return new Promise((resolve, reject) => {
        Office.context.document.getFileAsync(Office.FileType.Compressed, { sliceSize: SLICE_BYTES }, (res) => {
            var _a, _b;
            if (res.status !== Office.AsyncResultStatus.Succeeded) {
                reject(new Error((_b = (_a = res.error) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : 'getFileAsync falhou'));
                return;
            }
            const file = res.value;
            const fatias = [];
            // O Office permite UM arquivo aberto por vez: fechar sempre, até na
            // falha, ou a próxima captura recebe "arquivo em uso".
            const terminar = (erro) => file.closeAsync(() => (erro ? reject(erro) : resolve(encodeDocxSnapshot(fatias, meta))));
            const lerFatia = (i) => {
                if (i >= file.sliceCount) {
                    terminar(null);
                    return;
                }
                file.getSliceAsync(i, (fatia) => {
                    var _a, _b;
                    if (fatia.status !== Office.AsyncResultStatus.Succeeded) {
                        terminar(new Error((_b = (_a = fatia.error) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : `fatia ${i} falhou`));
                        return;
                    }
                    fatias.push(fatia.value.data);
                    lerFatia(i + 1);
                });
            };
            lerFatia(0);
        });
    });
}
/**
 * Substitui o documento aberto pelo gravado.
 *
 * - `.docx` inteiro: `insertFileFromBase64(replace)` no nível do Document, com
 *   estilos, tema e propriedades — o mesmo caminho do template, que traz
 *   seção, cabeçalho e rodapé junto.
 * - OOXML legado (só o corpo): aplica ANTES o template do tipo, para os estilos,
 *   a seção e o cabeçalho/rodapé serem os da Norven, e só então põe o corpo
 *   gravado por cima. Sem isso os estilos do documento em branco vencem.
 *   Verificado no Word: Normal volta Muli, Título 1 volta Styrene roxo, e
 *   "primeira página diferente" volta ligado.
 *
 * Tracking suspenso nos dois: restaurar é voltar a um estado, não uma edição.
 */
export async function restoreDocumentFile(snapshot, documentType) {
    if (isDocxSnapshot(snapshot)) {
        const opcoes = {
            importStyles: true,
            importTheme: true,
            importPageColor: true,
            importParagraphSpacing: true,
        };
        // Opções de requirement sets mais novos só entram onde existem: um host que
        // não as conhece pode recusar a chamada inteira.
        if (Office.context.requirements.isSetSupported('WordApi', '1.6')) {
            // Leva junto o plano de tópicos gravado no arquivo; `setSkeleton` o
            // regrava logo depois a partir do banco, que é a fonte da conversa.
            opcoes.importCustomProperties = true;
        }
        if (Office.context.requirements.isSetSupported('WordApi', '1.7')) {
            opcoes.importDifferentOddEvenPages = true;
        }
        await withTrackChangesSuspended(async () => {
            await Word.run(async (context) => {
                context.document.insertFileFromBase64(docxSnapshotBase64(snapshot), Word.InsertLocation.replace, opcoes);
                await context.sync();
            });
        });
        return;
    }
    // Legado. Falhar o template não impede o corpo: volta com a formatação que
    // der, que ainda é melhor que não voltar.
    await applyTemplate(documentType, 'replace');
    await withTrackChangesSuspended(async () => {
        await Word.run(async (context) => {
            context.document.body.insertOoxml(snapshot, Word.InsertLocation.replace);
            await context.sync();
        });
    });
}
